import { intersect } from "@entrilia/shared/utilities/arrayHelper";
import {
  allChecklistTaskStatuses,
  type ChecklistTask,
  type ChecklistTaskStatus,
  type Field,
} from "../../../api/types/objectTypes";

// Identity/system fields edited outside the custom-field bag: Name/Category are shown in the header,
// Description has its own editor. Mirrors the editable-field set in Core's UpdateTaskDetailsAsync.
const checklistTaskReservedFieldIds = ["Name", "Category", "Description"];

export const getEditableChecklistTaskFields = (templateFields: Field[]): Field[] =>
  templateFields.filter(
    (field) => !field.attributes.includes("ReadOnly") && !checklistTaskReservedFieldIds.includes(field.id)
  );

interface TaskStatusOptions {
  primaryOption: ChecklistTaskStatus | "approval";
  secondaryOptions: ChecklistTaskStatus[];
}

interface TaskStatusFlags {
  canApprove: boolean;
  canChangeStatus: boolean;
}

export const getTaskStatusOptions = (
  currentStatus: ChecklistTaskStatus,
  { canApprove, canChangeStatus }: TaskStatusFlags
): TaskStatusOptions => {
  if (canApprove) {
    return {
      primaryOption: "approval",
      secondaryOptions: canChangeStatus ? allChecklistTaskStatuses.filter((s) => s !== currentStatus) : [],
    };
  }

  const primaryOption = currentStatus === "Done" ? "ToDo" : "Done";
  const secondaryOptions = allChecklistTaskStatuses.filter((s) => s !== currentStatus && s !== primaryOption);
  return {
    primaryOption,
    secondaryOptions,
  };
};

export const getAvailableBulkStatuses = (tasks: ChecklistTask[]): ChecklistTaskStatus[] => {
  if (tasks.length === 0) {
    return [];
  }

  const targetStatusesPerTask = tasks.map((task) => {
    if (!isTaskStatusChangeAllowed(task)) {
      return [];
    }

    return allChecklistTaskStatuses.filter((status) => status !== task.status);
  });

  const intersection = targetStatusesPerTask.reduce((result, statuses) => intersect(result, statuses));

  return intersection.sort((a, b) => allChecklistTaskStatuses.indexOf(a) - allChecklistTaskStatuses.indexOf(b));
};

export const isTaskApprovalAllowed = (task: ChecklistTask, userId: string): boolean =>
  !!task.approvalId &&
  task.status !== "ToDo" &&
  task.approvalStatus === "Pending" &&
  task.pendingApprovalFromUserIds.includes(userId);

export const isTaskStatusChangeAllowed = (task: ChecklistTask): boolean =>
  task.approvalStatus === undefined || task.approvalStatus === "Pending";
